import React, { useState, useEffect } from 'react';
import { Package, PlusCircle, Trash2, Save, Search, Edit, X } from 'lucide-react';
import { api } from '../services/apiClient';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    try {
      const data = await api.categories.list();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }

  async function handleAddCategory(e) {
    e.preventDefault();
    try {
      const data = await api.categories.create({ name: newCategory });
      setCategories([...categories, data]);
      setIsAdding(false);
      setNewCategory('');
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function handleUpdateCategory(id) {
    try {
      await api.categories.update(id, { name: editName });
      setCategories(categories.map(c => c.id === id ? { ...c, name: editName } : c));
      setEditingId(null);
      setEditName('');
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  async function handleDeleteCategory(id) {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria? Le illustrazioni associate diventeranno "Senza Categoria".')) return;

    try {
      await api.categories.delete(id);
      setCategories(categories.filter(c => c.id !== id));
    } catch (error) {
      alert(`Errore: ${error.message}`);
    }
  }

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-brand-dark">
          <Package size={24} />
          <h3 className="text-xl font-semibold">Gestione Categorie</h3>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all"
          >
            <PlusCircle size={18} /> Aggiungi Categoria
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm max-w-md mx-auto">
          <form onSubmit={handleAddCategory} className="flex gap-3">
            <input
              className="flex-1 p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
              placeholder="Nome categoria (es. Ritratti, Paesaggi...)"
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              required
            />
            <button type="submit" className="bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90">
              Salva
            </button>
            <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-500">
              Annulla
            </button>
          </form>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Cerca categoria..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {filteredCategories.length === 0 ? (
          <div className="col-span-full text-center py-10 text-gray-400 italic">
            Nessuna categoria trovata.
          </div>
        ) : (
          filteredCategories.map(cat => (
            <div key={cat.id} className="bg-white p-4 rounded-xl border border-brand-main shadow-sm flex justify-between items-center group">
              {editingId === cat.id ? (
                <div className="flex flex-1 gap-2">
                  <input
                    className="flex-1 p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    autoFocus
                  />
                  <button onClick={() => handleUpdateCategory(cat.id)} className="text-green-600 p-1"><Save size={18}/></button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 p-1"><X size={18}/></button>
                </div>
              ) : (
                <>
                  <span className="font-medium text-brand-dark">{cat.name}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}
                      className="text-blue-500 hover:text-blue-700 p-1"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoryManager;
